import React, { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const WEBHOOK_URL =
  "https://n8n.twodots.cloud/webhook/4fffbef6-4141-4a3f-8165-2b2f17c7841c/chat";

const DEFAULT_PROMPT = "Ask about a company, initiative, or growth signal...";

const parseWebhookResponse = (payload) => {
  if (typeof payload !== "object" || payload === null) {
    return String(payload);
  }

  if ("body" in payload && payload.body) {
    return payload.body;
  }

  const keys = ["output", "response", "message", "text", "result"];
  for (const key of keys) {
    if (key in payload && payload[key]) {
      return payload[key];
    }
  }

  return JSON.stringify(payload, null, 2);
};

const createSessionId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.floor(Math.random() * 100000)}`;
};

function CompanyResearchPage() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => createSessionId());

  const clearChat = () => {
    setMessages([]);
    setSessionId(createSessionId());
    setInputValue("");
  };

  const handleSend = async () => {
    if (!inputValue.trim()) {
      return;
    }

    const prompt = inputValue.trim();
    setInputValue("");
    setMessages((prev) => [...prev, { role: "user", content: prompt }]);
    setLoading(true);

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chatInput: prompt,
          sessionId
        })
      });

      if (!response.ok) {
        throw new Error(
          `Server returned ${response.status}: ${await response.text()}`
        );
      }

      const payload = await response.json().catch(() => null);
      const assistantReply = parseWebhookResponse(payload ?? {});

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: assistantReply }
      ]);
    } catch (error) {
      const errorMessage = `❌ ${error.message || "Unable to reach research agent."}`;
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: errorMessage }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const chatHint = useMemo(() => {
    return messages.length === 0
      ? "Enter any company name to start a research session."
      : "Ask follow-up questions anytime to prompt deeper context.";
  }, [messages.length]);

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
      <div style={styles.hero}>
        <h1 style={styles.title}>Company Research Assistant</h1>
        <div style={styles.heroActions}>
          <button type="button" onClick={clearChat} style={styles.clearButton}>
            Clear chat
          </button>
          <span style={styles.sessionIdLabel}>
            Session <code>{sessionId.slice(0, 8)}</code>
          </span>
        </div>
      </div>

      <div style={styles.chatContainer}>
        <div style={styles.statusRow}>
          <div style={styles.statusBadge}>
            {loading ? (
              <>
                <span style={styles.spinner} />
                <span>Researcher is thinking…</span>
              </>
            ) : (
              <span>{messages.length ? "Conversation ongoing" : "Ready for your question"}</span>
            )}
          </div>
          <span style={styles.tipText}>{chatHint}</span>
        </div>

        <div style={styles.chatWindow}>
          {messages.map((message, index) => {
            const isAssistant = message.role === "assistant";
            return (
              <div
                key={`${message.role}-${index}`}
                style={{
                  ...styles.messageRow,
                  justifyContent: isAssistant ? "flex-start" : "flex-end"
                }}
              >
                {isAssistant && (
                  <span style={{ ...styles.messageIcon, ...styles.agentIcon }}>🤖</span>
                )}
                <div
                  style={{
                    ...styles.chatBubble,
                    background: isAssistant
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(120,103,255,0.15)",
                    color: isAssistant ? "#f0f6ff" : "#ffe7c9"
                  }}
                >
                  <span style={styles.messageRole}>
                    {isAssistant ? "Research Agent" : "You"}
                  </span>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ node, ...props }) => (
                        <p style={{ margin: 0, lineHeight: 1.7 }} {...props} />
                      ),
                      strong: ({ node, ...props }) => (
                        <strong style={{ fontWeight: 700 }} {...props} />
                      ),
                      em: ({ node, ...props }) => (
                        <em style={{ fontStyle: "italic" }} {...props} />
                      ),
                      code: ({ node, inline, ...props }) => (
                        <code
                          style={{
                            background: "rgba(255,255,255,0.1)",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 13
                          }}
                          {...props}
                        />
                      ),
                      a: ({ node, ...props }) => (
                        <a
                          style={{ color: "#8cf2ff" }}
                          target="_blank"
                          rel="noreferrer"
                          {...props}
                        >
                          {props.children}
                        </a>
                      ),
                      li: ({ node, ...props }) => (
                        <li style={{ marginTop: 6 }} {...props} />
                      )
                    }}
                  >
                    {message.content ?? ""}
                  </ReactMarkdown>
                </div>
                {!isAssistant && (
                  <span style={{ ...styles.messageIcon, ...styles.userIcon }}>👤</span>
                )}
              </div>
            );
          })}
        {loading && (
          <div style={{ ...styles.messageRow, justifyContent: "flex-start" }}>
            <span style={{ ...styles.messageIcon, ...styles.agentIcon }}>🤖</span>
            <div style={{ ...styles.chatBubble, background: "rgba(255,255,255,0.05)" }}>
              <span style={styles.messageRole}>Research Agent</span>
              <p style={styles.spinnerLine}>
                <span style={styles.spinner} />
                Analyzing company signals...
              </p>
            </div>
          </div>
        )}
        </div>

        <div style={styles.inputRow}>
          <input
            type="text"
            placeholder={DEFAULT_PROMPT}
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            style={styles.inputField}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleSend();
              }
            }}
            disabled={loading}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!inputValue.trim() || loading}
            style={styles.sendButton}
          >
            {loading ? "Sending…" : "Send"}
          </button>
        </div>

        <div style={styles.helpText}>
          Tokenized requests go directly to the webhook above. Use follow-up prompts
          to uncover hiring, partnership, or growth insights.
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    width: "100%",
    padding: "0 24px 48px",
    boxSizing: "border-box"
  },
  hero: {
    maxWidth: 960,
    margin: "0 auto 24px",
    textAlign: "center"
  },
  title: {
    fontSize: 38,
    margin: 0,
    color: "#f4f7ff"
  },
  subtitle: {
    marginTop: 8,
    color: "#b0bcff",
    fontSize: 16
  },
  heroActions: {
    marginTop: 16,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 12
  },
  clearButton: {
    padding: "10px 22px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.4)",
    background: "transparent",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 600
  },
  sessionIdLabel: {
    fontSize: 12,
    color: "#8ea1ff"
  },
  chatContainer: {
    maxWidth: 920,
    margin: "0 auto",
    background: "rgba(9,11,24,0.6)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 28,
    padding: 20,
    boxShadow: "0 20px 60px rgba(0,0,0,0.45)"
  },
  statusRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 12
  },
  statusBadge: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.05)",
    color: "#d1e0ff",
    fontSize: 13
  },
  tipText: {
    fontSize: 13,
    color: "#8da3ff"
  },
  chatWindow: {
    minHeight: 280,
    borderRadius: 20,
    border: "1px solid rgba(255,255,255,0.08)",
    padding: 14,
    background: "rgba(5,6,18,0.9)",
    maxHeight: "50vh",
    overflowY: "auto"
  },
  messageRow: {
    marginBottom: 16,
    display: "flex",
    alignItems: "flex-start",
    gap: 12
  },
  chatBubble: {
    maxWidth: 520,
    padding: 10,
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,0.1)",
    boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
    fontSize: 14
  },
  messageIcon: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    background: "rgba(255,255,255,0.08)"
  },
  agentIcon: {
    color: "#8ef0ff"
  },
  userIcon: {
    color: "#ffd27d"
  },
  messageRole: {
    display: "block",
    fontSize: 12,
    letterSpacing: 1,
    textTransform: "uppercase",
    color: "#8da3ff",
    marginBottom: 4
  },
  spinnerLine: {
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#f0f6ff",
    fontSize: 14
  },
  inputRow: {
    marginTop: 18,
    display: "flex",
    gap: 10,
    flexWrap: "wrap"
  },
  inputField: {
    flex: 1,
    minWidth: 220,
    padding: "12px 16px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.2)",
    background: "rgba(255,255,255,0.04)",
    color: "#fff",
    fontSize: 15
  },
  sendButton: {
    borderRadius: 14,
    border: "none",
    background: "linear-gradient(120deg, #ff7f50, #b86fff)",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    padding: "12px 22px",
    cursor: "pointer",
    boxShadow: "0 12px 20px rgba(184,111,255,0.35)"
  },
  helpText: {
    marginTop: 16,
    fontSize: 13,
    color: "#8fa3ff",
    lineHeight: 1.4
  },
  spinner: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    border: "2px solid #fff",
    borderTopColor: "transparent",
    animation: "spin 0.8s linear infinite",
    display: "inline-block",
    marginRight: 6,
    verticalAlign: "middle"
  }
};

export default CompanyResearchPage;
